import { createContext, useContext, useCallback, useState, useRef, useEffect, type ReactNode } from "react";
import { Check, AlertCircle, X } from "lucide-react";

// ── Types ───────────────────────────────────────────────────────

export interface Toast {
  id: string;
  type: "success" | "error";
  message: string;
}

interface ToastContextValue {
  success: (message: string) => void;
  error: (message: string) => void;
  dismiss: (id: string) => void;
}

// ── Context ─────────────────────────────────────────────────────

const ToastContext = createContext<ToastContextValue | null>(null);

const noopToast: ToastContextValue = {
  success: () => {},
  error: () => {},
  dismiss: () => {},
};

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  return ctx ?? noopToast;
}

// ── Provider ────────────────────────────────────────────────────

let nextId = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback(
    (type: "success" | "error", message: string) => {
      const id = `toast-${++nextId}`;
      const toast: Toast = { id, type, message };
      setToasts((prev) => [...prev, toast]);

      if (type === "success") {
        const timer = setTimeout(() => {
          timers.current.delete(id);
          setToasts((prev) => prev.filter((t) => t.id !== id));
        }, 4000);
        timers.current.set(id, timer);
      }
    },
    []
  );

  const success = useCallback((msg: string) => addToast("success", msg), [addToast]);
  const error = useCallback((msg: string) => addToast("error", msg), [addToast]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      timers.current.forEach((t) => clearTimeout(t));
      timers.current.clear();
    };
  }, []);

  return (
    <ToastContext.Provider value={{ success, error, dismiss }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

// ── Toast container (fixed bottom-right) ────────────────────────

function ToastContainer({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: string) => void }) {
  if (toasts.length === 0) return null;

  return (
    <div
      aria-live="polite"
      className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 max-w-sm"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

// ── Individual toast ────────────────────────────────────────────

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: string) => void }) {
  const isSuccess = toast.type === "success";

  return (
    <div
      role="alert"
      className={`flex items-start gap-2 px-3 py-2.5 rounded-lg shadow-lg border text-[12px] ${
        isSuccess
          ? "bg-emerald-50 border-emerald-200 text-emerald-800"
          : "bg-red-50 border-red-200 text-red-800"
      }`}
    >
      {isSuccess ? (
        <Check className="size-3.5 shrink-0 mt-0.5 text-emerald-600" />
      ) : (
        <AlertCircle className="size-3.5 shrink-0 mt-0.5 text-red-600" />
      )}
      <span className="flex-1 break-words">{toast.message}</span>
      <button
        onClick={() => onDismiss(toast.id)}
        className={`shrink-0 p-0.5 rounded hover:bg-black/5 ${
          isSuccess ? "text-emerald-500" : "text-red-500"
        }`}
        aria-label="Dismiss notification"
      >
        <X className="size-3" />
      </button>
    </div>
  );
}
